// ──────────────────────────────────────────────────────────────
// auth.js — Sign-in, Account Dashboard & Upgrade Flow
// ──────────────────────────────────────────────────────────────

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Single source of truth for all localStorage key names.
 *  Prevent silent breakage from scattered magic strings. */
const AUTH_KEYS = {
  token:     'mediprice_token',
  pro:       'mediprice_pro',
  email:     'mediprice_email',
  expiresAt: 'mediprice_expires_at',
  checks:    'mediprice_checks',
  plan:      'mediprice_plan',
};

// Cached for the "Copy referral link" button.
let cachedReferralCode = null;

function syncReferralMainVisibility(loggedIn) {
  const authPanel = document.getElementById('referral-main-auth');
  const loggedOutPanel = document.getElementById('referral-main-loggedout');
  if (authPanel) authPanel.hidden = !loggedIn;
  if (loggedOutPanel) loggedOutPanel.hidden = loggedIn;
}

/** Format ISO date string to a friendly display date */
function formatExpiry(isoString) {
  if (!isoString) return null;
  const d = new Date(isoString);
  if (isNaN(d)) return null;
  return d.toLocaleDateString('en-ZA', { day: 'numeric', month: 'long', year: 'numeric' });
}

export function getAuthState() {
  const token = localStorage.getItem(AUTH_KEYS.token);
  // Token validity loosely relies on its presence since we also track checks_remaining.
  // The actual hard verification happens on the backend.
  const loggedIn = !!token;
  const isPro = localStorage.getItem(AUTH_KEYS.pro) === 'true';
  const checks = parseInt(localStorage.getItem(AUTH_KEYS.checks) || '0', 10);
  const email = localStorage.getItem(AUTH_KEYS.email) || '';
  const expiresAt = localStorage.getItem(AUTH_KEYS.expiresAt) || null;
  const plan = localStorage.getItem(AUTH_KEYS.plan) || 'pro_yearly';

  return { loggedIn, isPro, checks, email, expiresAt, plan };
}

/** Check if the user is currently logged in with a token */
function isSessionValid() {
  return !!localStorage.getItem(AUTH_KEYS.token);
}

export function updateAuthUI() {
  const { loggedIn, isPro } = getAuthState();

  const navBtn         = document.getElementById('nav-auth-btn');
  const mobileBtn      = document.getElementById('mobile-auth-btn');
  const navUpgrade     = document.getElementById('nav-upgrade-btn');
  const mobileUpgrade  = document.getElementById('mobile-upgrade-btn');

  if (loggedIn) {
    if (navBtn)    navBtn.textContent    = 'Account';
    if (mobileBtn) mobileBtn.textContent = 'Account';
    if (navUpgrade) navUpgrade.textContent = 'Upgrade';
    if (mobileUpgrade) mobileUpgrade.textContent = 'Upgrade to Pro';
    if (navUpgrade)    navUpgrade.hidden = isPro;
    if (mobileUpgrade) mobileUpgrade.hidden = isPro;
    
    // Toggle upgrade-related prompts based on Pro status
    document.querySelectorAll('.pro-price-hideable').forEach(el => {
      el.hidden = isPro;
      el.style.display = isPro ? 'none' : ''; 
    });
  } else {
    if (navBtn)    navBtn.textContent    = 'Sign in';
    if (mobileBtn) mobileBtn.textContent = 'Sign in';
    if (navUpgrade) navUpgrade.textContent = 'Pricing';
    if (mobileUpgrade) mobileUpgrade.textContent = 'Pricing';
    if (navUpgrade)    navUpgrade.hidden = false;
    if (mobileUpgrade) mobileUpgrade.hidden = false;
  }

  syncReferralMainVisibility(loggedIn);

  window.dispatchEvent(new CustomEvent('mediprice-auth-changed'));
}


export function showAuthModal(message = null, isError = false) {
  const modal = document.getElementById('auth-modal');
  if (!modal) return;

  const state = getAuthState();
  const loginView = document.getElementById('auth-view-login');
  const account   = document.getElementById('auth-account');

  // Reset status
  const statusEl = document.getElementById('auth-status');
  if (statusEl) {
    statusEl.hidden = true;
    statusEl.textContent = '';
    statusEl.className = 'auth-modal__status';
    if (message && typeof message === 'string' && message.trim()) {
      statusEl.textContent = message;
      statusEl.classList.add(isError ? 'auth-modal__status--error' : 'auth-modal__status--success');
      statusEl.hidden = false;
    }
  }

  if (state.loggedIn) {
    if (loginView) loginView.hidden = true;
    if (account) {
      account.hidden = false;
      renderAccountView(state);
    }
  } else {
    if (account) account.hidden = true;
    if (loginView) {
      loginView.hidden = false;
      renderLoginView();
    }
  }

  modal.hidden = false;
  // Trigger entry animation without forcing synchronous reflow (defers layout read to next frame)
  const card = modal.querySelector('.auth-modal__card');
  if (card) {
    card.style.animation = 'none';
    requestAnimationFrame(() => {
      card.offsetHeight; // layout read in next frame
      card.style.animation = '';
    });
  }

}

function renderLoginView() {
  const emailInput = document.getElementById('auth-email');
  const submitBtn  = document.getElementById('auth-submit-btn');
  const submitText = document.getElementById('auth-submit-text');
  const spinner    = document.querySelector('.auth-modal__spinner');
  
  if (emailInput) { 
    emailInput.value = ''; 
    emailInput.classList.remove('is-invalid', 'is-valid'); 
  }
  if (submitBtn)  submitBtn.disabled = false;
  if (submitText) submitText.textContent = 'Send Login Link';
  if (spinner)    spinner.style.display = 'none';
  
  setTimeout(() => emailInput?.focus(), 50);
}

function renderAccountView(state) {
  const { isPro, checks, email, expiresAt, plan } = state;

  // Avatar & Header
  const avatarEl = document.getElementById('auth-avatar-initial');
  if (avatarEl) avatarEl.textContent = (email[0] || 'M').toUpperCase();

  const emailEl = document.getElementById('auth-user-email');
  if (emailEl) emailEl.textContent = email || 'Your Account';

  const statusPill = document.getElementById('auth-status-pill');
  if (statusPill) {
    statusPill.textContent = isPro ? '✦ Pro Member' : 'Free Plan';
    statusPill.className = isPro ? 'pro-pill pro-pill--active' : 'pro-pill';
  }

  // Feature status badges
  updateFeatureBadge('feature-script-checker', 'feat-script-status', isPro, checks > 0, isPro ? 'Included' : (checks > 0 ? 'Enabled' : 'Pro only'));
  updateFeatureBadge('feature-best-price', 'feat-price-status', isPro, checks > 0, isPro ? 'Included' : (checks > 0 ? 'Enabled' : 'Pro only'));
  updateFeatureBadge('feature-checks-remaining', 'feat-checks-status', isPro, checks > 0, isPro ? 'Unlimited' : `${checks}`);

  // Upgrade vs Pro stats cards
  const upgradeCard = document.getElementById('upgrade-card');
  const proStats    = document.getElementById('pro-stats');
  if (upgradeCard) upgradeCard.hidden = isPro;
  if (proStats)    proStats.hidden    = !isPro;

  // Plan Details
  const isActiveYearly = isPro && plan === 'pro_yearly' && expiresAt && new Date(expiresAt) > new Date();
  const proBuyOndemandBtn = document.getElementById('auth-buy-ondemand-btn-pro');
  if (proBuyOndemandBtn) {
    proBuyOndemandBtn.style.display = isActiveYearly ? 'none' : 'block';
  }

  if (isPro && expiresAt && new Date(expiresAt) > new Date()) {
    const expiryEl = document.getElementById('auth-expiry-date');
    const planEl   = document.getElementById('auth-plan');
    const friendly = formatExpiry(expiresAt);
    
    if (expiryEl && friendly) expiryEl.textContent = `Expires: ${friendly}`;
    if (planEl) {
      // Use CSS classes instead of inline styles for maintainability
      planEl.textContent = '';
      const statusSpan = document.createElement('span');
      statusSpan.className = 'pro-card__active';
      statusSpan.textContent = 'Active';
      planEl.append('Status: ', statusSpan);
      if (friendly) {
        const renewSmall = document.createElement('small');
        renewSmall.className = 'pro-card__renew-date';
        renewSmall.textContent = `(Renews ${friendly})`;
        planEl.append(' ', renewSmall);
      }
    }
  }

  // Fetch and update referral stats asynchronously
  fetchReferralStats();
}

async function fetchReferralStats() {
  const token = localStorage.getItem(AUTH_KEYS.token);
  if (!token) return;
  try {
    const res = await fetch('/api/referral-get-code', {
      headers: { 'Authorization': `Bearer ${token}` }
    });

    if (res.status === 401) {
      handleLogout();
      showAuthModal('Session expired. Please sign in again to continue.', true);
      return;
    }

    if (res.ok) {
      const data = await res.json();
      cachedReferralCode = data.referralCode || null;

      // Account-only counter (modal)
      const earnedEl = document.getElementById('referral-credits-earned');
      const maxEl = document.getElementById('referral-credits-max');
      const earned = Number(data.creditsEarned);
      const cap = Number(data.maxCredits);
      const earnedSafe = Number.isFinite(earned) ? Math.max(0, earned) : 0;
      const capSafe = Number.isFinite(cap) ? Math.max(0, cap) : 0;

      if (earnedEl) earnedEl.textContent = String(earnedSafe);
      if (maxEl) maxEl.textContent = String(capSafe);

      // Main page counter + copy button state
      const earnedMainEl = document.getElementById('referral-credits-earned-main');
      const maxMainEl = document.getElementById('referral-credits-max-main');
      const copyBtn = document.getElementById('referral-copy-link-btn');
      const copyStatus = document.getElementById('referral-copy-status');
      if (earnedMainEl) earnedMainEl.textContent = String(earnedSafe);
      if (maxMainEl) maxMainEl.textContent = String(capSafe);

      const maxedOut = capSafe > 0 && earnedSafe >= capSafe;
      if (copyBtn) copyBtn.disabled = !cachedReferralCode || maxedOut;
      if (copyStatus) {
        if (!cachedReferralCode) copyStatus.textContent = '';
        else if (maxedOut) copyStatus.textContent = 'Max reached — invites won\'t award more credits.';
        else copyStatus.textContent = '';
      }

      const mainInviteBtn = document.getElementById('referral-main-invite-btn');
      const mainEmail = document.getElementById('referral-main-email');
      const modalInviteBtn = document.getElementById('referral-submit-btn');
      const modalEmail = document.getElementById('referral-email');
      if (mainInviteBtn) mainInviteBtn.disabled = maxedOut;
      if (mainEmail) mainEmail.disabled = maxedOut;
      if (modalInviteBtn) modalInviteBtn.disabled = maxedOut;
      if (modalEmail) modalEmail.disabled = maxedOut;
    }
  } catch (e) {
    // silently fail
  }
}

function updateFeatureBadge(rowId, statusId, isPro, hasChecks, text) {
  const row = document.getElementById(rowId);
  const status = document.getElementById(statusId);
  const isActive = isPro || hasChecks;
  
  if (row) row.className = isActive ? 'account-feature account-feature--active' : 'account-feature';
  if (status) {
    status.textContent = text;
    status.className = isActive ? 'account-feature__status account-feature__status--active' : 'account-feature__status';
  }
}

export function closeAuthModal() {
  const modal = document.getElementById('auth-modal');
  if (modal) modal.hidden = true;
}

export function handleLogout() {
  Object.values(AUTH_KEYS).forEach(key => localStorage.removeItem(key));
  cachedReferralCode = null;
  updateAuthUI();
  closeAuthModal();
}

/** After a successful payment, re-verify with the server to confirm Pro status */
async function refreshProFromServer() {
  const token = localStorage.getItem(AUTH_KEYS.token);
  if (!token) return;
  try {
    const res = await fetch('/api/auth-verify', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token })
    });
    const data = await res.json().catch(() => ({}));
    if (res.ok && data.ok) {
      if (data.isPro) {
        localStorage.setItem(AUTH_KEYS.pro, 'true');
      } else {
        localStorage.removeItem(AUTH_KEYS.pro);
      }
      if (data.expires_at) localStorage.setItem(AUTH_KEYS.expiresAt, data.expires_at);
      if (data.email) localStorage.setItem(AUTH_KEYS.email, data.email);
      if (data.checks_remaining !== undefined) localStorage.setItem(AUTH_KEYS.checks, data.checks_remaining);
      if (data.plan) localStorage.setItem(AUTH_KEYS.plan, data.plan);
    }
  } catch {
    // silently fail
  }
}

export function initAuth() {
  try {
    const refParam = new URLSearchParams(window.location.search).get('ref');
    if (refParam && refParam.trim()) {
      sessionStorage.setItem('mediprice_pending_ref', refParam.trim());
    }
  } catch (_) {
    /* ignore */
  }

  const hash = window.location.hash;

  if (hash === '#checkout=success') {
    // Optimistically set Pro if checking out a yearly plan.
    // However, since we don't know the plan type from the hash, we 
    // rely entirely on the server refresh to get the true state.
    history.replaceState(null, '', window.location.pathname + window.location.search);
    
    // We run refresh immediately. Once complete it updates the UI.
    refreshProFromServer().then(() => {
      updateAuthUI();
      showAuthModal('🎉 Payment successful! Your account is now updated.', false);
    });
  } else if (hash === '#checkout=cancelled') {
    history.replaceState(null, '', window.location.pathname + window.location.search);
    showAuthModal('Checkout was cancelled. No charge was made.', true);
  } else if (hash === '#pro=1') {
    localStorage.setItem(AUTH_KEYS.pro, 'true');
    history.replaceState(null, '', window.location.pathname + window.location.search);
    updateAuthUI();
    showAuthModal('Welcome to MediPrice Pro! 🎉', false);
  }

  updateAuthUI();

  // ── Referral main page actions ──
  document.getElementById('referral-signin-btn')?.addEventListener('click', () => {
    showAuthModal();
  });

  async function postReferralInvite(friendEmail) {
    const token = localStorage.getItem(AUTH_KEYS.token);
    if (!token) return { ok: false, status: 401, error: 'Please sign in.' };
    const res = await fetch('/api/referral-invite', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ friendEmail }),
    });
    const data = await res.json().catch(() => ({}));
    if (res.ok) return { ok: true, data };
    return { ok: false, status: res.status, error: data.error || 'Could not send invite.' };
  }

  document.getElementById('referral-copy-link-btn')?.addEventListener('click', async () => {
    const statusEl = document.getElementById('referral-copy-status');

    if (!cachedReferralCode) {
      await fetchReferralStats();
    }

    if (!cachedReferralCode) {
      if (statusEl) statusEl.textContent = 'Please sign in to generate your referral link.';
      return;
    }

    const referralLink = `${window.location.origin}/?ref=${encodeURIComponent(cachedReferralCode)}`;

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(referralLink);
      } else {
        // Fallback for older browsers
        const ta = document.createElement('textarea');
        ta.value = referralLink;
        ta.setAttribute('readonly', '');
        ta.style.position = 'absolute';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }

      if (statusEl) {
        statusEl.textContent = 'Copied!';
        setTimeout(() => { if (statusEl) statusEl.textContent = ''; }, 2500);
      }
    } catch {
      if (statusEl) statusEl.textContent = 'Could not copy link. Please copy manually.';
    }
  });

  // Populate the main referral UI immediately (so it isn't hidden until you open Account).
  if (isSessionValid()) fetchReferralStats();

  // ── Navigation click handlers ──
  document.getElementById('nav-auth-btn')?.addEventListener('click', () => showAuthModal());
  document.getElementById('nav-upgrade-btn')?.addEventListener('click', () => {
    // Logged-out users usually want to understand plans first; logged-in users
    // can go straight to the upgrade flow in the modal.
    if (isSessionValid()) showAuthModal();
    else document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });
  document.getElementById('mobile-auth-btn')?.addEventListener('click', () => {
    document.getElementById('mobile-nav')?.setAttribute('aria-hidden', 'true');
    showAuthModal();
  });
  document.getElementById('mobile-upgrade-btn')?.addEventListener('click', () => {
    document.getElementById('mobile-nav')?.setAttribute('aria-hidden', 'true');
    if (isSessionValid()) showAuthModal();
    else document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });

  document.querySelectorAll('.buy-pro-btn').forEach(btn => btn.addEventListener('click', () => showAuthModal()));
  document.querySelectorAll('.buy-ondemand-btn').forEach(btn => btn.addEventListener('click', () => showAuthModal()));

  document.getElementById('auth-modal-close')?.addEventListener('click', closeAuthModal);
  document.getElementById('auth-modal')?.querySelector('.auth-modal__backdrop')?.addEventListener('click', closeAuthModal);
  document.getElementById('auth-logout-btn')?.addEventListener('click', handleLogout);

  // Sync auth state across tabs
  window.addEventListener('storage', (e) => {
    if (e.key && e.key.startsWith('mediprice_')) {
      // Re-run the UI and modal updates for any auth-related Change.
      // We use a tiny debounce to handle the multiple setItem calls from the other tab (link auth.html).
      if (window._authSyncTimer) clearTimeout(window._authSyncTimer);
      window._authSyncTimer = setTimeout(() => {
        updateAuthUI();
        
        const modal = document.getElementById('auth-modal');
        if (modal && !modal.hidden) {
          showAuthModal(); // Switching from Login view to Account view if logged in
        }

        // If explicitly logged out (token removed), ensure modal is closed
        if (e.key === AUTH_KEYS.token && !e.newValue) {
          closeAuthModal();
        }
      }, 50);
    }
  });

  // ── Login Form ──
  const form       = document.getElementById('auth-form');
  const emailInput = document.getElementById('auth-email');
  const submitBtn  = document.getElementById('auth-submit-btn');
  const submitText = document.getElementById('auth-submit-text');
  const spinner    = document.querySelector('.auth-modal__spinner');
  const statusEl   = document.getElementById('auth-status');

  // Real-time email validation on blur
  emailInput?.addEventListener('blur', () => {
    const val = emailInput.value.trim();
    if (!val) return; // Don't validate empty on blur
    if (!EMAIL_RE.test(val)) {
      emailInput.classList.add('is-invalid');
      emailInput.classList.remove('is-valid');
    } else {
      emailInput.classList.remove('is-invalid');
      emailInput.classList.add('is-valid');
    }
  });
  emailInput?.addEventListener('input', () => {
    // Clear error state while typing
    emailInput.classList.remove('is-invalid');
  });

  if (form) {
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      if (!emailInput || !submitBtn || !statusEl) return;

      const email = emailInput.value.trim();

      // Client-side validation BEFORE sending
      if (!email) {
        showStatus(statusEl, 'Please enter your email address.', true);
        emailInput.focus();
        return;
      }
      if (!EMAIL_RE.test(email)) {
        showStatus(statusEl, 'Please enter a valid email address (e.g. name@example.com).', true);
        emailInput.classList.add('is-invalid');
        emailInput.focus();
        return;
      }

      // Show loading state
      submitBtn.disabled = true;
      if (submitText) submitText.textContent = 'Sending link…';
      if (spinner) spinner.style.display = 'inline-block';
      statusEl.hidden = true;

      const params = new URLSearchParams(window.location.search);
      let ref = params.get('ref');
      if (!ref) {
        try {
          ref = sessionStorage.getItem('mediprice_pending_ref');
        } catch (_) {
          ref = null;
        }
      }
      const payload = { email };
      if (ref && ref.trim()) payload.ref = ref.trim();

      try {
        const res = await fetch('/api/auth-send-link', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });

        const data = await res.json().catch(() => ({}));

        if (res.ok) {
          // Success - lock form and show it
          showStatus(statusEl, '✓ Login link sent! Check your inbox (and spam folder).', false);
          if (submitText) submitText.textContent = '📬 Check your inbox';
          emailInput.disabled = true;
          // Don't re-enable the button, prevents double submissions
        } else if (res.status === 429) {
          showStatus(statusEl, 'Too many attempts, please wait a few minutes and try again.', true);
          submitBtn.disabled = false;
          if (submitText) submitText.textContent = 'Send Login Link';
          if (spinner) spinner.style.display = 'none';
        } else {
          showStatus(statusEl, data.error || 'Something went wrong. Please try again.', true);
          submitBtn.disabled = false;
          if (submitText) submitText.textContent = 'Send Login Link';
          if (spinner) spinner.style.display = 'none';
        }
      } catch {
        showStatus(statusEl, 'Network error. Please check your connection and try again.', true);
        submitBtn.disabled = false;
        if (submitText) submitText.textContent = 'Send Login Link';
        if (spinner) spinner.style.display = 'none';
      }
    });
  }

  // ── Checkout triggers ──
  async function triggerCheckout(btn, planType) {
    const btnText = btn.querySelector('span') || btn;
    const token = localStorage.getItem('mediprice_token');
    const statusEl = document.getElementById('auth-status');
    if (!token) return;

    const originalText = btn.textContent;
    btn.disabled = true;
    btnText.textContent = 'Preparing checkout…';

    try {
      const res = await fetch('/api/payfast-create-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ plan: planType })
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.url) {
        window.location.href = data.url;
      } else {
        if (statusEl) showStatus(statusEl, data.error || 'Could not start checkout. Please try again.', true);
        btn.disabled = false;
        btnText.textContent = originalText;
      }
    } catch {
      if (statusEl) showStatus(statusEl, 'Network error. Please check your connection.', true);
      btn.disabled = false;
      btnText.textContent = originalText;
    }
  }

  document.getElementById('auth-upgrade-btn')?.addEventListener('click', (e) => triggerCheckout(e.currentTarget, 'yearly'));
  document.getElementById('auth-buy-ondemand-btn')?.addEventListener('click', (e) => triggerCheckout(e.currentTarget, 'ondemand'));
  document.getElementById('auth-buy-ondemand-btn-pro')?.addEventListener('click', (e) => triggerCheckout(e.currentTarget, 'ondemand'));

  // ── Referral forms (Account modal + home section) ──
  const referralForm = document.getElementById('referral-form');
  const refEmailInput = document.getElementById('referral-email');
  const refSubmitBtn = document.getElementById('referral-submit-btn');
  const refStatusEl = document.getElementById('referral-status');

  const mainInviteForm = document.getElementById('referral-main-invite-form');
  const mainEmailInput = document.getElementById('referral-main-email');
  const mainInviteBtn = document.getElementById('referral-main-invite-btn');
  const mainInviteStatus = document.getElementById('referral-main-invite-status');

  async function handleReferralInviteSubmit(friendEmail, statusEl, submitBtn, emailInput) {
    if (!friendEmail || !EMAIL_RE.test(friendEmail)) {
      showStatus(statusEl, 'Please enter a valid email address.', true);
      return;
    }
    const originalLabel = submitBtn.textContent;
    submitBtn.disabled = true;
    submitBtn.textContent = 'Sending…';
    statusEl.hidden = true;

    try {
      const result = await postReferralInvite(friendEmail);
      if (result.ok && result.data) {
        const data = result.data;
        showStatus(
          statusEl,
          "If they can join via this invite, they'll get an email. You both earn credits when they sign up (per programme settings).",
          false,
        );
        if (emailInput) emailInput.value = '';
        submitBtn.textContent = originalLabel;
        submitBtn.disabled = false;
        await fetchReferralStats();
        /* fetchReferralStats applies cap / session disabled state */
      } else if (result.status === 401) {
        handleLogout();
        showAuthModal('Session expired. Please sign in again to continue.', true);
        submitBtn.disabled = false;
        submitBtn.textContent = originalLabel;
      } else {
        showStatus(statusEl, result.error || 'Could not send invite.', true);
        submitBtn.disabled = false;
        submitBtn.textContent = originalLabel;
      }
    } catch {
      showStatus(statusEl, 'Network error. Please try again.', true);
      submitBtn.disabled = false;
      submitBtn.textContent = originalLabel;
    }
  }

  if (referralForm && refEmailInput && refSubmitBtn && refStatusEl) {
    referralForm.addEventListener('submit', async (e) => {
      e.preventDefault();
      await handleReferralInviteSubmit(refEmailInput.value.trim(), refStatusEl, refSubmitBtn, refEmailInput);
    });
  }

  if (mainInviteForm && mainEmailInput && mainInviteBtn && mainInviteStatus) {
    mainInviteForm.addEventListener('submit', async (e) => {
      e.preventDefault();
      await handleReferralInviteSubmit(mainEmailInput.value.trim(), mainInviteStatus, mainInviteBtn, mainEmailInput);
    });
  }
}

// Helper: update the status element
function showStatus(el, message, isError) {
  el.textContent = message;
  el.className = `auth-modal__status auth-modal__status--${isError ? 'error' : 'success'}`;
  el.hidden = false;
}
